import type { CSSProperties } from 'vue'

/**
 * How tall the docked panel's column is, and where it starts — MYAC-1287.
 *
 * The `<aside>` is in normal flow, so at page top it begins under the app
 * header and its sticky column is that much shorter than the viewport; once
 * scrolled, the header is gone, the offset is 0 and the column already fills
 * the screen.
 *
 * Beside a FULL-SCREEN dialog that is wrong. The dialog runs from the very top
 * of the viewport to the bottom, and a panel one header short beside it reads
 * as a chat box that shrank (Sy, 2026-09-16). So the column takes the whole
 * viewport and is pulled up over the header by the offset it would otherwise
 * have given away — the header is under the dialog's mask anyway, dimmed and
 * out of play.
 *
 * A module rather than a computed in the component, for the same reason
 * `chatBesideLayout` is one: the arithmetic is the part worth pinning, and
 * jsdom measures every element at zero, so a test through the component could
 * only ever see the offset-0 case — which is the one case where both branches
 * agree.
 */

/**
 * Whether the column reaches over the app header.
 *
 * Both conditions matter. Without a full-screen dialog the header is the page's
 * own and the panel has no business covering it; with an offset of 0 there is
 * nothing above the column to cover.
 */
export function reachesOverHeader(topOffset: number, besideFullScreenDialog: boolean): boolean {
  return besideFullScreenDialog && topOffset > 0
}

/** The column's height, and the pull-up that makes it start at the viewport's top. */
export function dockedColumnStyle(
  topOffset: number,
  besideFullScreenDialog: boolean,
): CSSProperties {
  return reachesOverHeader(topOffset, besideFullScreenDialog)
    ? { height: '100vh', marginTop: `-${topOffset}px` }
    : { height: `calc(100vh - ${topOffset}px)` }
}

/**
 * Where the FLOATING panel (overlay / takeover) starts — MYAC-1484.
 *
 * The Drawer is fixed to the viewport, so left alone it starts at the very top
 * and covers the app header: on a 1024px window the panel hid the header's
 * right-most menus (Regulatory monitoring) and its buttons. It takes the same
 * line as the docked column instead — under the header and banners at page
 * top, the whole viewport once they have scrolled away — and the same
 * exception: beside a full-screen dialog it runs the whole height.
 *
 * Applied to the Drawer's mask, which holds the panel and sizes it.
 */
export function floatingMaskBounds(
  topOffset: number,
  besideFullScreenDialog: boolean,
): CSSProperties {
  const top = reachesOverHeader(topOffset, besideFullScreenDialog) ? 0 : topOffset
  return { top: `${top}px`, height: `calc(100% - ${top}px)` }
}
